import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import { isBefore, isAfter, subDays, parseISO } from 'date-fns';
import flowRight from 'lodash-es/flowRight';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import { lookupsJson, getNonRenewalNotificationDays, everspanAndSCORStatesMapToPolicyType } from '@ourbranch/lookups';
import { EFFECTIVE_DATE_LOCKED_HINT, EFFECTIVE_DATE_LOCKED_MESSAGE } from '@ourbranch/policy-utils';
import { policyTypes as pTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { getTodayStr, isFutureDate } from '@ourbranch/date-fns-helpers';

import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import FormList from 'core/components/form-list';
import { Tooltip } from 'core/components/tooltip';
import { track } from 'core/helpers/analytics';
import { AddInterestedPartyForm, AddLienHoldersForm, InterestedParty, LienHolders } from 'common/components/people';
import { interestedPartyListLabels } from 'common/helpers/auto-interested-party-helpers';
import { AddFarmInterestedPartyForm, FarmInterestedParty } from 'common/components/farm/additional-party';
import {
  getLocationOptions,
  getEquipmentOptions,
  getRiskOptions,
  getSppOptions,
  getRpsOptions,
  getFarmPolicyMask,
  getAddInterestedPartyLabel
} from 'common/helpers/farm-helpers';
import { makePolicyDetailsFromSegments } from '@ourbranch/farm-utils';
import { CognitoPermissionGroups, PermissionRequireTypes } from 'core/helpers/cognito-permission-groups';
import { Button } from 'core/components/button';
import { Card } from 'core/components/card';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import Loading from 'core/components/loading/loading';
import withDatePicker from 'core/components/with-date-picker';
import { dateFormatter } from 'core/helpers/formatters';
import { showExpirationInsteadOfEndDate } from 'core/helpers/expiration-date';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { showsLastInspectionField } from 'core/helpers/policy-type';
import { useDisabledState } from 'common/disabled-context';

import useStyles from './policy-settings.styles';
import { staffOfferLink } from 'core/helpers/bridge-link-opened';
import { brandConfig } from 'brand-config';
import { buildSupplementalDocumentOptions } from './supplemental-documents';
import { getAgentCancellationNote } from './agent-cancellation';
import AgentCancellationNote from './agent-cancellation-note';

const padWithZeros = (str) => str.toString().padStart(3, 0);

function getLastModifiedBy(policy) {
  return Array.isArray(policy.versionHistory)
    ? `${dateFormatter(new Date(policy.versionHistory[policy.versionHistory.length - 1].updatedDateTime))} by ${
        policy.versionHistory[policy.versionHistory.length - 1].username
      }`
    : 'Unknown';
}

const PolicySettings = ({
  changed,
  segment,
  formik,
  handleAddHoldCard,
  minDate,
  maxDate,
  cancelledOrInCancellation
}) => {
  const classes = useStyles();
  const {
    account: {
      policies: {
        policy: { allActiveHoldCards, getAllowLicensedActions }
      }
    }
  } = useStore();
  const { values: policy, setFieldValue, setFieldTouched } = formik;

  const farmDetails = useMemo(
    () => (isFarmBasedPolicyType(policy.policyType) ? makePolicyDetailsFromSegments(policy, policy.segments) : {}),
    [policy]
  );

  const locationOptions = useMemo(() => getLocationOptions(farmDetails.farmLocations || []), [farmDetails]);
  const equipmentOptions = useMemo(() => getEquipmentOptions(farmDetails.farmLocations || []), [farmDetails]);
  const riskOptions = useMemo(
    () => getRiskOptions(farmDetails.farmLocations || [], policy.policyType, policy.state),
    [farmDetails, policy.policyType, policy.state]
  );
  const sppOptions = useMemo(() => getSppOptions(farmDetails.farmLocations || []), [farmDetails]);
  const rpsOptions = useMemo(() => getRpsOptions(farmDetails.farmLocations || []), [farmDetails]);

  // #34376: optional supplemental documents an underwriter can attach to this policy. Catalog is
  // per-program (brand-config); Forreston's Branch Agreement is offered only on migrated renewals,
  // and each entry's policyTypes scope decides which policies offer it (#35891 — note `brandConfig`
  // from 'brand-config' is already the inner config object).
  const supplementalDocumentOptions = useMemo(
    () =>
      buildSupplementalDocumentOptions(brandConfig?.supplementalDocuments, {
        isMigratedRenewal: !!policy.wasRenewedFromImportedTerm,
        policyType: policy.policyType
      }),
    [policy.wasRenewedFromImportedTerm, policy.policyType]
  );

  // Title for the farm interested-party list comes from the policy-type mask so Forreston Rental (Y)
  // and Home (Z) show "Additional Insured/Interest". (#31367)
  const farmPolicyMask = isFarmBasedPolicyType(policy.policyType)
    ? getFarmPolicyMask(policy.policyType, policy.state)
    : null;
  const farmInterestedPartyTitle = farmPolicyMask?.interestedPartyLabel || 'Additional Interests/Insureds';
  // "Farm" is dropped from the add-party button on Home (Z) / Rental (Y); kept for full Farm. (#32045)
  const addInterestedPartyLabel = getAddInterestedPartyLabel(policy.policyType);
  // Relationships the mask removes from the dropdown for the current liability type
  // (e.g. GL-4002 on rentals, #32043).
  const farmHiddenRelationships =
    farmPolicyMask?.hiddenInterestedPartyRelationships?.[farmDetails.farmLiabilityCoverage?.type] || [];
  // FO-41 add-on toggle is removed entirely on Rental (#32359).
  const farmHideFO41 = Boolean(farmPolicyMask?.hideFO41);
  // Loss Payee "Associated equipment" field is dropped on Home/Rental (#32521/#32525); full Farm
  // keeps it (#32520).
  const farmHideLossPayeeEquipment = Boolean(farmPolicyMask?.hideLossPayeeEquipment);

  const minEndDate = new Date(formik.values.effectiveDate);
  // The field's minimum is one day after the effective date. When showing the EXPIRATION date
  // (= stored endDate + 1), bump the minimum by an extra day so the earliest selectable value still
  // maps back to endDate = effectiveDate + 1 (a real 1-day term) rather than endDate = effectiveDate
  // (a flat cancellation, which must go through the dedicated cancellation flow). (#32070, devlo)
  const minEndDateOffset = showExpirationInsteadOfEndDate() ? 2 : 1;
  minEndDate.setDate(new Date(formik.values.effectiveDate).getDate() + minEndDateOffset);

  const session = useSession();

  /* Only allow users in CanAutoRenew cognito group to toggle auto renew.
    If auto renew toggle is turned off, a user with the CanAutoRenew permission can turn it back on, regardless of state notification requirements.
  */
  const disableAutoRenewToggle = () => {
    if (!session.canAutoRenew) {
      return true;
    }
    if (everspanAndSCORStatesMapToPolicyType[policy.state]?.includes(policy.policyType) && !policy.autoRenew) {
      return true;
    }
    if (session.canAutoRenew && (!policy.autoRenew || policy?.renewalVariables?.allowForcedNonRenewal)) {
      return false;
    }

    const currentDate = parseISO(getTodayStr(policy.state));
    if ([pTypes.Home, pTypes.Auto].includes(policy.policyType)) {
      // give a 1-day mailing buffer
      const notificationDays =
        getNonRenewalNotificationDays(policy.state, policy.policyType, policy.endDate, {
          policyInceptionDate: policy.inceptionDate || policy.effectiveDate
        }) + 1;
      if (
        isBefore(currentDate, parseISO(policy.endDate)) &&
        isAfter(currentDate, subDays(parseISO(policy.endDate), notificationDays))
      ) {
        return true;
      }
    }
  };

  const { disabled, setDisabled } = useDisabledState();

  // The effective date is LOCKED once the policy has become effective (#36450): it has been reported
  // (written premium, for one) on that date, and moving it would make the books stop tying out. Only a
  // future-effective policy may move, and only with CanEditEffectiveDate; a renewal term (term >= 2)
  // never moves — its date is the prior term's end. The locked field says so and points at
  // cancel-rewrite. policy-mod's validateEvent rejects the move server-side regardless.
  const effectiveDateLocked = getPolicyStatus(policy) !== PolicyStatus.Future;
  const disabledEditEffectiveDate = effectiveDateLocked || !session.canEditEffectiveDate || policy.term >= 2;

  /* Only disable when checkbox checked or policy loaded with those dates
    or if there are any hold cards on the policy or if the policy is cancelled
  */
  const hasHoldCards = !!allActiveHoldCards().length;
  const disabledForm =
    !!formik.values.cancel || (cancelledOrInCancellation && !changed) || !session.canEditPolicies || hasHoldCards;

  // internal staff users can cancel policies if they are licensed for the policy state
  // external staff users can cancel policies even if they are unlicensed as long as they are not view only
  // https://github.com/gobranch/branch/issues/25665
  const canCancelPolicy = session.isAgency ? !session.viewOnly : getAllowLicensedActions(session);
  // #37502: on CFM and Forreston agents send the insured's signed request to underwriting instead.
  const agentCancellationNote = getAgentCancellationNote(brandConfig, { isAgency: session.isAgency });

  useEffect(() => {
    setDisabled(disabledForm);
  }, [disabledForm, setDisabled]);

  const onCancel = useCallback(() => {
    setFieldValue('cancel', true);
    setFieldTouched('cancel');
    const policyType = lookupsJson.policyTypes.find((policyType) => policyType.id === policy.policyType);
    track('Staff Cancellation Initiated', {
      policyType: policyType.value,
      policyId: policy.id,
      isFutureEffective: isFutureDate(policy.effectiveDate)
    });
  }, [setFieldValue, policy.cancel]);

  useEffect(() => {
    if (!formik.values.expirationDate) {
      const vals = {
        expirationDate: policy.endDate,
        lastModified: getLastModifiedBy(policy)
      };
      formik.setValues({ ...formik.values, ...vals });
    }

    if (typeof formik.values.sendPaperDocuments === 'undefined') {
      const segments = [...policy.segments].reverse();
      const segmentWithDiscountInfo = segments.find((item) => typeof item?.global?.discountPaperless === 'boolean');
      const sendPaperDocuments = !segmentWithDiscountInfo.global.discountPaperless;

      formik.setValues({ ...formik.values, sendPaperDocuments });
    }
  }, [formik, policy, disabled]);

  const endDateDisabled = useCallback(
    (effectiveDate) => {
      if (session.canBackDate) {
        return false;
      }
      const currentDateTime = new Date(new Date().toISOString().slice(0, 10)).getTime();
      const effectiveDateTime = new Date(effectiveDate).getTime();
      if (effectiveDateTime >= currentDateTime) {
        return false;
      }
      return true;
    },
    [session.canBackDate]
  );

  if (!segment || !formik.values.expirationDate) {
    return <Loading type="secondary" />;
  }
  // Which interested-party list a policy edits: people on a Home/Condo, mortgagees/interests on a
  // farm-family policy, vehicle interests on an Auto.
  const renderAdditionalParties = () => {
    if (policy.policyType === pTypes.Home || policy.policyType === pTypes.Condo) {
      return (
        <FormList
          key={`parties-${disabled}`}
          id="additionalParties"
          item={InterestedParty}
          addForm={AddInterestedPartyForm}
          title="Additional interested party"
          addLabel="Add People"
          disabled={disabled || session.viewOnly}
        />
      );
    }
    if (isFarmBasedPolicyType(policy.policyType)) {
      return (
        <FormList
          key={`farm-parties-${disabled}`}
          id="farmInterestedParties"
          item={FarmInterestedParty}
          addForm={AddFarmInterestedPartyForm}
          addButtonLabel={addInterestedPartyLabel}
          title={farmInterestedPartyTitle}
          singular="Interest/Insured"
          plural="Interests/Insureds"
          hiddenRelationships={farmHiddenRelationships}
          hideFO41={farmHideFO41}
          hideLossPayeeEquipment={farmHideLossPayeeEquipment}
          disabled={disabled || session.viewOnly}
          locationOptions={locationOptions}
          equipmentOptions={equipmentOptions}
          riskOptions={riskOptions}
          sppOptions={sppOptions}
          rpsOptions={rpsOptions}
          isPolicy
          includeFarmLiability={farmDetails.includeFarmLiability}
          liabilityType={farmDetails.farmLiabilityCoverage?.type}
        />
      );
    }
    return (
      <FormList
        key={`lien-holders-${disabled}`}
        id="additionalParties"
        item={LienHolders}
        addForm={AddLienHoldersForm}
        {...interestedPartyListLabels()}
        disabled={disabled || session.viewOnly}
      />
    );
  };

  return (
    <>
      <Card key={`settings-${disabled}`} type="secondary" className={classes.card}>
        {formik.values.expirationDate && (
          <Grid container justifyContent="space-around" alignItems="flex-start" spacing={2}>
            <Grid item xs={12} key="labelPolicyTerm">
              <Label key="lblTop" type="formSubTitle" className={classes.label}>
                Policy Term
              </Label>
            </Grid>
            <Grid
              container
              key="row1"
              justifyContent={policy.effectiveDate !== policy.endDate ? 'space-between' : 'start'}
              spacing={2}
              className={classes.row}
            >
              <FormField
                name="effectiveDate"
                type="date"
                label="Start Date"
                mode="dark"
                xs={3}
                disabled={disabledEditEffectiveDate}
                disableFuture={false}
                minDate={minDate}
                maxDate={maxDate}
                {...(effectiveDateLocked
                  ? {
                      // A short hint under the narrow field; the full reason in the tooltip (devlo, #36459).
                      helperText: (
                        <>
                          {EFFECTIVE_DATE_LOCKED_HINT}{' '}
                          <span className={classes.effectiveDateHelp}>
                            <Tooltip text={EFFECTIVE_DATE_LOCKED_MESSAGE} placement="bottom" smallIcon={{}} />
                          </span>
                        </>
                      )
                    }
                  : {})}
              />
              {policy.effectiveDate !== policy.endDate && (
                <FormField
                  name="endDate"
                  type="date"
                  label={showExpirationInsteadOfEndDate() ? 'Policy Expiration Date' : 'Last Full Day Of Coverage'}
                  mode="dark"
                  minDate={minEndDate}
                  xs={3}
                  disabled={endDateDisabled(policy.effectiveDate)}
                  disableFuture={false}
                  fast={false}
                />
              )}
              <FormField
                name="rewriteReason"
                type="select"
                label="Rewrite"
                mode="dark"
                xs={6}
                options={lookupsJson.rewriteReason}
              />
            </Grid>

            <Grid item xs={12} key="labelPolicyInformation">
              <Label key="lblPolicyInformation" type="formSubTitle" className={classes.label}>
                Policy Information
              </Label>
            </Grid>
            <Grid container key="policyInformation" className={classes.row} justifyContent="space-between">
              <FormField name="state" type="value" label="State" mode="dark" xs={4} />
              <FormField name="term" type="value" label="Term" mode="dark" xs={1} formatter={padWithZeros} />
              <FormField name="version" type="value" label="Version" mode="dark" xs={2} formatter={padWithZeros} />
              <FormField name="lastModified" type="value" label="Last Modified" mode="dark" xs={5} />
            </Grid>
            <Grid container key="policyOfferInformation" className={classes.row} spacing={4}>
              <FormField
                name="salesRep"
                type="value"
                label="Producer"
                mode="dark"
                xs={4}
                formatter={(producer) => producer || 'N/A'}
              />
              <FormField
                name="offer.quote.leadSource"
                type="select"
                label="Lead Source"
                mode="dark"
                options={lookupsJson.leadSources}
                xs={4}
              />
              {!session.isAgency && (
                <FormField
                  name="offerId"
                  type="value"
                  label="Offer Id"
                  mode="dark"
                  xs={4}
                  link={policy.offerId ? staffOfferLink(policy.offerId) : null}
                />
              )}
              {/* #33158: last inspection date on non-auto policies. Internal-only (hidden from
                  independent agents); editable by underwriting. Supports the 3-year inspection
                  rotation. Optional; populated by UW, migration, or the renewal creator. */}
              {!session.isAgency && showsLastInspectionField(policy.policyType) && (
                <FormField
                  name="lastInspectionDate"
                  type="date"
                  label="Last Inspection Date"
                  mode="dark"
                  xs={4}
                  disableFuture
                  permissions={{
                    edit: { groups: [CognitoPermissionGroups.canApproveUWReview] }
                  }}
                  defaultToEmpty
                />
              )}
            </Grid>

            <Grid item xs={12} key="labelPolicyActions">
              <Label key="lblPolicyActions" type="formSubTitle" className={classes.labelWithDivider}>
                Policy Actions
              </Label>
            </Grid>
            <Grid container key="policyActions" justifyContent="space-between" className={classes.row}>
              <Grid container item xs={8} key="policyActionsLeft">
                <FormField
                  xs={12}
                  type="switch"
                  name="autoRenew"
                  label="Auto Renew"
                  mode="dark"
                  className={classes.toggleRow}
                  disabled={disableAutoRenewToggle()}
                  permissions={{
                    isLicensedAction: false
                  }}
                  onChange={(_, val) => {
                    setFieldValue('nonRenewed', !val);
                    if (!val) {
                      // only change if turning off auto renew
                      setFieldValue('isNonRenew', true);
                    }
                  }}
                />
                <FormField
                  xs={12}
                  type="switch"
                  name="renewalCreditPull"
                  label="Run Credit/Insurance Score at Renewal"
                  mode="dark"
                  className={classes.toggleRow}
                  permissions={{
                    edit: { groups: [CognitoPermissionGroups.isService] }
                  }}
                />
                {!session.isAgency && (
                  <FormField
                    xs={12}
                    type="switch"
                    name="sendPaperDocuments"
                    label="Send Paper Documents"
                    mode="dark"
                    className={classes.toggleRow}
                    permissions={{
                      edit: {
                        groups: [CognitoPermissionGroups.canSendPaperDocs]
                      }
                    }}
                    tooltipLabel="More Info"
                    tooltipText={
                      !session.canSendPaperDocs ? 'Please follow your designated process to perform this action.' : null
                    }
                  />
                )}
                <FormField
                  xs={12}
                  type="switch"
                  name="blockCreditCheckAtRenewal"
                  label="Block Credit Check at Renewal for Hardship"
                  mode="dark"
                  className={classes.toggleRow}
                  permissions={{
                    edit: {
                      groups: [CognitoPermissionGroups.isInternalAgent, CognitoPermissionGroups.canToggleCreditCheck],
                      type: PermissionRequireTypes.All
                    }
                  }}
                />
                {session.canToggleIsAgencyPolicy && (
                  <FormField
                    xs={12}
                    type="switch"
                    name="isAgentSold"
                    label="Agency Sold Policy"
                    mode="dark"
                    className={classes.toggleRow}
                    tooltipLabel="More Info"
                    tooltipText="This toggle marks the policy as agency-sold for servicing. If the Agency banner doesn’t match the toggle, try flipping it ON and OFF (or OFF and ON) before updating the policy."
                    permissions={{
                      edit: {
                        groups: [CognitoPermissionGroups.canToggleIsAgencyPolicy],
                        type: PermissionRequireTypes.All
                      }
                    }}
                  />
                )}
                {session?.canToggleHomeRepairsRequested && policy.policyType === pTypes.Home && (
                  <FormField
                    xs={12}
                    type="switch"
                    name="homeInspectionRepairsRequested"
                    label="Cancel if proof of repair not received within URP"
                    mode="dark"
                    className={classes.toggleRow}
                    permissions={{
                      edit: {
                        groups: [CognitoPermissionGroups.canToggleHomeRepairsRequested],
                        type: PermissionRequireTypes.All
                      }
                    }}
                  />
                )}
                {/* #34376: optional supplemental documents attached to the policy/application package.
                    Internal-underwriter-only; options come from the per-program brand catalog, each
                    entry scoped to its policy types (#35891 adds auto for CFM's Recurring Payment
                    Authorization), and the selection clears at renewal. */}
                {!session.isAgency && supplementalDocumentOptions.length > 0 && (
                  <FormField
                    xs={12}
                    type="chipArray"
                    id="supplementalDocuments"
                    name="supplementalDocuments"
                    label="Supplemental Documents"
                    description="Optional documents attached to the policy package. Clears at renewal."
                    mode="dark"
                    options={supplementalDocumentOptions}
                    emptyMessage="No supplemental documents available"
                    permissions={{ isLicensedAction: false }}
                  />
                )}
              </Grid>
              <Grid container item xs={4} key="policyActionsRight" justifyContent="flex-end">
                <Button className={classes.button} disabled={!session?.canAddHoldCards} onClick={handleAddHoldCard}>
                  Add Hold Card
                </Button>
                {!cancelledOrInCancellation && agentCancellationNote && (
                  <AgentCancellationNote
                    note={agentCancellationNote}
                    email={brandConfig.agentCancellationRequestEmail}
                  />
                )}
                {!cancelledOrInCancellation && !agentCancellationNote && (
                  <div>
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={onCancel}
                      className={classes.secondaryButton}
                      disabled={!canCancelPolicy}
                    >
                      Cancel Policy
                    </Button>
                    {!canCancelPolicy && (
                      <Tooltip
                        className={classes.holdPaymentWarning}
                        text="You must be licensed in this policy's state to perform this action."
                        placement="bottom"
                        label="More Info"
                        light
                      />
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Card>
      {renderAdditionalParties()}
    </>
  );
};

PolicySettings.propTypes = {
  changed: PropTypes.bool.isRequired,
  formik: PropTypes.object.isRequired,
  accountId: PropTypes.string.isRequired,
  policy: PropTypes.shape({
    effectiveDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    billingDayOfMonth: PropTypes.number.isRequired,
    autoRenew: PropTypes.bool,
    state: PropTypes.string.isRequired,
    term: PropTypes.number.isRequired,
    version: PropTypes.number.isRequired
  }).isRequired,
  segment: PropTypes.object,
  handleAddHoldCard: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date).isRequired,
  maxDate: PropTypes.instanceOf(Date).isRequired,
  cancelledOrInCancellation: PropTypes.bool.isRequired
};

PolicySettings.defaultProps = {
  segment: null
};

export default flowRight(withDatePicker, connect, observer)(PolicySettings);
